import { Money } from "@/components/Money";
import { Mono, ExternalMono } from "@/components/Mono";
import { Timestamp } from "@/components/Timestamp";
import { RelatedLink } from "@/components/RelatedLink";
import { formatReceiveAmount, receiveAddressEntity, receiveTokenSymbol } from "@/lib/receive";
import { col } from "../columns";
import { chainName } from "@/lib/chains";
import { txUrl } from "@/lib/explorer";
import { COLLECTIONS } from "@/firestore/collections";
import type {
  CrossChainDepositDoc,
  CrossChainSendDoc,
  DepositAddressDoc,
  WithDocId,
} from "@/firestore/types";
import type { EntityConfig } from "../types";
import { monoCol, moneyCol, relatedCol, stateCol, textCol, timeCol } from "../shared";

type P<T> = WithDocId<T>;

// ─────────────────────────────── Deposit Address ───────────────────────────────

export const depositAddressConfig: EntityConfig<DepositAddressDoc> = {
  key: "depositAddress",
  collection: COLLECTIONS.depositAddresses,
  title: "Deposit Address",
  pluralTitle: "Deposit Addresses",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<DepositAddressDoc>>("id", "ID", (r) => r.id),
    stateCol<P<DepositAddressDoc>>((r) => r.state),
    textCol<P<DepositAddressDoc>>("origin", "Origin", (r) => chainName(r.originChainId)),
    textCol<P<DepositAddressDoc>>("destination", "Destination", (r) => chainName(r.destinationChainId)),
    textCol<P<DepositAddressDoc>>("sourceToken", "Source asset", receiveTokenSymbol),
    textCol<P<DepositAddressDoc>>("token", "Receive asset", (r) => r.tokenSymbol),
    monoCol<P<DepositAddressDoc>>("depositAddress", "Deposit address", (r) => r.depositAddress),
    // `superseded` (wallet-service 0.78.0): replaced by the successor's address for the same route.
    col<P<DepositAddressDoc>>({
      id: "supersededBy",
      header: "Superseded by",
      value: (r) => r.supersededBy ?? "",
      cell: (r) => <RelatedLink entity={receiveAddressEntity(r)} id={r.supersededBy} />,
    }),
    relatedCol<P<DepositAddressDoc>>("wallet", "Wallet", "wallet", (r) => r.walletId),
    relatedCol<P<DepositAddressDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<DepositAddressDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Origin chain", value: chainName(d.originChainId) },
    { label: "Destination chain", value: chainName(d.destinationChainId) },
    { label: "Source asset", value: receiveTokenSymbol(d) },
    { label: "Source token contract / mint", value: <Mono value={d.originCurrency} copy /> },
    { label: "Receive asset", value: d.tokenSymbol },
    { label: "Deposit address", value: <Mono value={d.depositAddress} short copy /> },
    { label: "Destination address", value: <Mono value={d.destinationAddress} short copy /> },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.walletId} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    {
      // A superseded address still settles into the old Main; the credit is forwarded.
      label: "Superseded by",
      value: <RelatedLink entity={receiveAddressEntity(d)} id={d.supersededBy} />,
    },
  ],
  related: (d) => [
    {
      title: "Cross-chain deposits",
      entity: "crossChainDeposit",
      collection: COLLECTIONS.crossChainDeposits,
      fkField: "depositAddressId",
      value: d.id,
      orderBy: [["createdAt", "desc"]],
    },
  ],
};

// USDT routes have their own projection so older mobile clients only discover USDC addresses.
export const stablecoinReceiveAddressConfig: EntityConfig<DepositAddressDoc> = {
  ...depositAddressConfig,
  key: "stablecoinReceiveAddress",
  collection: COLLECTIONS.stablecoinReceiveAddresses,
  title: "USDT Receive Address",
  pluralTitle: "USDT Receive Addresses",
};

// ─────────────────────────────── Cross-Chain Deposit ───────────────────────────────

export const crossChainDepositConfig: EntityConfig<CrossChainDepositDoc> = {
  key: "crossChainDeposit",
  collection: COLLECTIONS.crossChainDeposits,
  title: "Cross-Chain Deposit",
  pluralTitle: "Cross-Chain Deposits",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<CrossChainDepositDoc>>("id", "ID", (r) => r.id),
    stateCol<P<CrossChainDepositDoc>>((r) => r.state),
    textCol<P<CrossChainDepositDoc>>("origin", "Origin", (r) => chainName(r.originChainId)),
    textCol<P<CrossChainDepositDoc>>("destination", "Destination", (r) => chainName(r.destinationChainId)),
    textCol<P<CrossChainDepositDoc>>("sourceToken", "Source asset", receiveTokenSymbol),
    textCol<P<CrossChainDepositDoc>>("sourceAmount", "Reported source amount", formatReceiveAmount),
    moneyCol<P<CrossChainDepositDoc>>("amount", "Reported output (USDC)", (r) => r.reportedAmountMicroUsdc),
    col<P<CrossChainDepositDoc>>({
      id: "depositAddress",
      header: "Deposit address",
      value: (r) => r.depositAddressId,
      cell: (r) => <RelatedLink entity={receiveAddressEntity(r)} id={r.depositAddressId} />,
    }),
    relatedCol<P<CrossChainDepositDoc>>("wallet", "Wallet", "wallet", (r) => r.walletId),
    relatedCol<P<CrossChainDepositDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<CrossChainDepositDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Origin chain", value: chainName(d.originChainId) },
    { label: "Destination chain", value: chainName(d.destinationChainId) },
    { label: "Source asset", value: receiveTokenSymbol(d) },
    { label: "Source token contract / mint", value: <Mono value={d.originCurrency} copy /> },
    { label: "Reported source amount", value: formatReceiveAmount(d) },
    { label: "Reported output (USDC)", value: <Money micro={d.reportedAmountMicroUsdc} fw={600} /> },
    { label: "Deposit address", value: <RelatedLink entity={receiveAddressEntity(d)} id={d.depositAddressId} /> },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.walletId} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    {
      label: "Origin tx",
      value: <Mono value={d.originTxHash} short copy />,
    },
    {
      label: "Destination tx (HyperEVM)",
      value: <ExternalMono value={d.destinationTxHash} href={txUrl(d.destinationTxHash)} />,
    },
    { label: "Refund tx", value: <Mono value={d.refundTxHash} short copy /> },
    { label: "Failure reason", value: d.failureReason ?? "—", full: true },
  ],
  // Parent deposit address is linked from detailFields above; the reverse
  // (an address's inbound deposits) lives on depositAddressConfig.related.
};

// ─────────────────────────────── Cross-Chain Send ───────────────────────────────

export const crossChainSendConfig: EntityConfig<CrossChainSendDoc> = {
  key: "crossChainSend",
  collection: COLLECTIONS.crossChainSends,
  title: "Cross-Chain Send",
  pluralTitle: "Cross-Chain Sends",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<CrossChainSendDoc>>("id", "ID", (r) => r.id),
    stateCol<P<CrossChainSendDoc>>((r) => r.state),
    textCol<P<CrossChainSendDoc>>("destination", "Destination", (r) => chainName(r.destinationChainId)),
    textCol<P<CrossChainSendDoc>>("token", "Token", (r) => r.tokenSymbol),
    moneyCol<P<CrossChainSendDoc>>("pay", "Pay", (r) => r.payAmountMicroUsdc),
    moneyCol<P<CrossChainSendDoc>>("receive", "Receive", (r) => r.receiveAmountMicroUsdc),
    moneyCol<P<CrossChainSendDoc>>("fee", "Fee", (r) => r.feeMicroUsdc),
    relatedCol<P<CrossChainSendDoc>>("wallet", "Wallet", "wallet", (r) => r.walletId),
    relatedCol<P<CrossChainSendDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<CrossChainSendDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Destination chain", value: chainName(d.destinationChainId) },
    { label: "Token", value: d.tokenSymbol },
    { label: "Recipient", value: <Mono value={d.recipientAddress} short copy /> },
    { label: "Pay amount", value: <Money micro={d.payAmountMicroUsdc} fw={600} /> },
    { label: "Receive amount", value: <Money micro={d.receiveAmountMicroUsdc} /> },
    { label: "Fee", value: <Money micro={d.feeMicroUsdc} /> },
    { label: "Relay order", value: <Mono value={d.relayOrderId} short copy /> },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.walletId} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    {
      label: "Source tx (HyperEVM)",
      value: <ExternalMono value={d.sourceTxHash} href={txUrl(d.sourceTxHash)} />,
    },
    {
      label: "Destination tx",
      value: <Mono value={d.destinationTxHash} short copy />,
    },
    { label: "Expires at", value: <Timestamp value={d.expiresAt} absolute /> },
    { label: "Failure reason", value: d.failureReason ?? "—", full: true },
  ],
};
